import { useCallback, useEffect, useRef, useState } from "react";

import { SamlOAuthService } from "@shared/modules/auth/services/SamlOAuthService";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { useTranslation } from "@shared/utils/internationalization";
import Icon from "@ui-kit/icons/Icon.web";
import { AuthProfileRepository } from "@shared/modules/auth/AuthProfileRepository";
import { useQueryClient } from "react-query";
import { AnalyticsService } from "@shared/services/analytics";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import Box from "@ui-kit/layout/Box/Box.web";
import Button from "@ui-kit/form/Button/Button.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Form from "@shared/components/application/form/Form/Form.web";
import { FORM_FIELD_TYPES } from "@shared/components/application/form/FormField/types";
import { validationPresets } from "@shared/components/application/form/utils/validation";
import { useMobileWebviewContext } from "@shared/contexts/MobileWebviewContext/MobileWebviewContext";

const authProfileRepo = new AuthProfileRepository();
const notifier = NotifierFactory.createNotifier();

export type SsoLoginFormProps = {
  onSuccess: () => void;
  onBack: () => void;
};

export default function SsoLoginForm({
  onSuccess,
  onBack,
}: SsoLoginFormProps): JSX.Element {
  const { t } = useTranslation("common");
  const [isLoading, setIsLoading] = useState(false);
  const { isMobileWebviewActive } = useMobileWebviewContext();
  const queryClient = useQueryClient();
  const { setLoggedInStates } = useUserSessionAuthState();

  const broadcastChannel = useRef(
    new BroadcastChannel(SamlOAuthService.AUTH_CHANNEL)
  );

  const fields = {
    email: {
      name: "email",
      type: FORM_FIELD_TYPES.EMAIL,
      label: t("Email address"),
      placeholder: t("Enter your work email"),
      defaultValue: "",
      validationRules: validationPresets.email(t),
      testId: "sso-email-input",
    },
  };

  useEffect(() => {
    const bc = broadcastChannel.current;

    const handleMessage = async (event: MessageEvent): Promise<void> => {
      if (event.data === SamlOAuthService.MESSAGES.USER_AUTHENTICATED) {
        AnalyticsService.buttonClick({
          custom_params: { datum: "authenticated" },
          label: "saml_login_button",
        });

        await queryClient.invalidateQueries(authProfileRepo.repositoryName);
        setLoggedInStates?.();
        onSuccess();
        setIsLoading(false);
      }
    };

    bc.addEventListener("message", handleMessage);
    return () => bc.removeEventListener("message", handleMessage);
  }, [onSuccess, queryClient, setLoggedInStates]);

  const openSamlLogin = useCallback(
    async (email: string): Promise<void> => {
      setIsLoading(true);

      let organizationSlug: string;
      try {
        organizationSlug = await SamlOAuthService.getOrganizationSlug(email);
      } catch (_error) {
        setIsLoading(false);
        notifier.error(
          t("This email provider is not supported for SSO login.")
        );
        return;
      }

      try {
        AnalyticsService.buttonClick({
          custom_params: { datum: "open" },
          label: "saml_login_button",
        });

        const authUrl = await SamlOAuthService.getAuthUrl(organizationSlug);

        if (isMobileWebviewActive) {
          window.location.href = authUrl;
          return;
        }

        const width = 600;
        const height = 600;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const authWindow = window.open(
          authUrl,
          "SamlAuthWindow",
          `width=${width}, height=${height}, top=${top}, left=${left}`
        );

        if (!authWindow) {
          setIsLoading(false);
          notifier.error(
            t(
              "Failed to open the authentication window. Please disable your popup blocker and try again."
            )
          );
          return;
        }

        const checkClosed = setInterval(() => {
          if (authWindow.closed) {
            clearInterval(checkClosed);
            setIsLoading(false);
          }
        }, 500);
      } catch (_error) {
        setIsLoading(false);
        notifier.error(t("Failed to initiate SSO login. Please try again."));
      }
    },
    [isMobileWebviewActive, t]
  );

  return (
    <Box width="100%">
      <Form
        fields={fields}
        onSubmit={({ email }: { email: string }) => openSamlLogin(email)}
        submitButtonText={t("Sign in")}
        submitButtonLoadingText={t("Signing in...")}
        submitButtonTestId="sso-sign-in-button"
        submitButtonProps={{ width: "100%", isLoading }}
      />

      <Flex justifyContent="center">
        <Button
          mt="lg"
          variant="link"
          color="fg.subtle"
          fontSize="sm"
          onClick={onBack}
          data-testid="sso-back-button"
        >
          <Icon.ChevronLeft boxSize="element.md" />
          {t("Back to login")}
        </Button>
      </Flex>
    </Box>
  );
}
